(() => {
  'use strict';

  const roots = document.querySelectorAll('[data-sb-nightlevel-pdp-v1]');

  roots.forEach((root) => {
    if (root.dataset.initialised === 'true') return;
    root.dataset.initialised = 'true';

    const one = (selector, scope = root) => scope.querySelector(selector);
    const all = (selector, scope = root) => Array.from(scope.querySelectorAll(selector));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const previewMode = document.documentElement.dataset.nightlevelPreview === 'true';

    const productForm = one('#sb-nightlevel-pdp-v1-form');
    const primaryAtc = one('[data-primary-atc]', productForm || root);
    const variantInput = one('[data-variant-input]', productForm || root);
    const quantityInput = one('[data-quantity-input]', productForm || root);
    const sizeInputs = all('input[name="nightlevel-size"]');
    const colourInputs = all('input[name="nightlevel-colour"]');
    const packInputs = all('input[name="nightlevel-pack"]');
    const bundleSizeInputs = all('[data-bundle-sizes] select');
    const globalSizePicker = one('[data-size-picker]');
    const deliveryDate = one('[data-delivery-date]');

    if (deliveryDate) {
      // Arrival = three UK working days after today (Mon to Fri count, weekends and England
      // and Wales bank holidays do not). Monday shows Thursday, Friday and the weekend show Wednesday.
      // Any failure keeps the server-rendered "the third working day" text.
      try {
        // England and Wales bank holidays to the end of 2027. Past that horizon the count still
        // skips weekends, only the holiday skip lapses, so extend this list each autumn.
        const bankHolidays = new Set([
          '2026-12-25', '2026-12-28', '2027-01-01', '2027-03-26', '2027-03-29',
          '2027-05-03', '2027-05-31', '2027-08-30', '2027-12-27', '2027-12-28',
        ]);
        const londonToday = Object.fromEntries(
          new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Europe/London', year: 'numeric', month: 'numeric', day: 'numeric',
          }).formatToParts(new Date()).map(({ type, value }) => [type, Number(value)]),
        );
        const arrival = new Date(Date.UTC(londonToday.year, londonToday.month - 1, londonToday.day));
        let workingDays = 0;
        while (workingDays < 3) {
          arrival.setUTCDate(arrival.getUTCDate() + 1);
          const weekday = arrival.getUTCDay();
          const isoDay = arrival.toISOString().slice(0, 10);
          if (weekday !== 0 && weekday !== 6 && !bankHolidays.has(isoDay)) workingDays += 1;
        }
        const dateFormatter = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'short',
        });
        deliveryDate.textContent = dateFormatter.format(arrival).replace('Sept', 'Sep');
      } catch (_error) {
        // Keep the Liquid fallback text.
      }
    }

    const offerMatrix = {
      1: {
        regular: { now: 3995, compare: 7995 },
        high: { now: 4495, compare: 8495 },
      },
      2: {
        regular: { now: 6995, compare: 15990 },
        high: { now: 7995, compare: 16990 },
      },
      4: {
        regular: { now: 11995, compare: 31980 },
        high: { now: 13995, compare: 33980 },
      },
    };
    const packActionLabels = {
      1: 'Try Nightlevel',
      2: 'Try Nightlevel',
      4: 'Try Nightlevel',
    };

    // Optional mixed packs use real bundle variants, never full-price singles or order notes.
    [2, 4].forEach((pack) => {
      for (let high = 1; high < pack; high += 1) {
        offerMatrix[pack][`${pack - high} regular + ${high} high`] = {
          now: offerMatrix[pack].regular.now + high * 500,
          compare: offerMatrix[pack].regular.compare + high * 500,
        };
      }
    });

    let variants = [];
    try {
      variants = JSON.parse(one('[data-product-variants]')?.textContent || '[]');
    } catch (_error) {
      variants = [];
    }

    const money = (pence) => new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(pence / 100);

    const normaliseOptions = (variant) => {
      const values = Array.isArray(variant?.options) ? variant.options : [];
      return values.map((value) => String(value).trim().toLowerCase());
    };

    const sizeMatches = (options, size) => options.some((value) => (
      value === size || value === `${size} profile`
    ));
    const colourMatches = (options, colour) => options.some((value) => (
      value === colour || (colour === 'grey' && value === 'gray') || value === `${colour} cover`
    ));
    const packMatches = (options, pack) => {
      const labels = pack === 1
        ? ['1', '1 pillow', 'single', 'one pillow']
        : [String(pack), `${pack} pillow`, `${pack} pillows`, `pack of ${pack}`];
      return options.some((value) => labels.includes(value));
    };

    const matchingVariants = (size, colour, pack) => {
      const offer = offerMatrix[pack]?.[size];
      if (!offer) return [];
      return variants.filter((variant) => {
        const options = normaliseOptions(variant);
        // The selling price must match exactly. The compare-at price is display-only:
        // Shopify drops compare_at_price for EU market contexts, so it must never block a sale.
        return sizeMatches(options, size)
          && colourMatches(options, colour)
          && packMatches(options, pack)
          && Number(variant.price) === offer.now;
      });
    };

    const expectedCells = [1, 2, 4].flatMap((pack) => (
      (variants.length === 18 ? ['regular', 'high'] : Object.keys(offerMatrix[pack])).flatMap((size) => (
        ['white', 'blue', 'grey'].map((colour) => ({ size, colour, pack }))
      ))
    ));
    const matchedVariants = expectedCells.map(({ size, colour, pack }) => matchingVariants(size, colour, pack));
    const variantIds = variants.map((variant) => String(variant?.id ?? '').trim());
    const commerceMatrixReady = variants.length === expectedCells.length
      && variants.every((variant) => normaliseOptions(variant).length === 3)
      && variantIds.every(Boolean)
      && new Set(variantIds).size === expectedCells.length
      && matchedVariants.every((matches) => matches.length === 1)
      && new Set(matchedVariants.flat().map((variant) => String(variant.id))).size === expectedCells.length;

    const findVariant = (size, colour, pack) => {
      if (!commerceMatrixReady) return null;
      const [variant] = matchingVariants(size, colour, pack);
      return variant && variant.available !== false
        ? { variant, quantity: 1, mode: 'size-colour-pack-price-verified' }
        : null;
    };

    const mixedBundlesReady = commerceMatrixReady && variants.length === 30;
    const sizeForPack = (pack, fallback) => {
      if (!mixedBundlesReady) return fallback;
      const sizes = all(`[data-bundle-sizes="${pack}"] select`).map((input) => input.value);
      if (sizes.length !== pack || sizes.some((size) => size !== 'regular' && size !== 'high')) return '';
      const high = sizes.filter((size) => size === 'high').length;
      return high === 0 ? 'regular' : high === pack ? 'high' : `${pack - high} regular + ${high} high`;
    };

    const currentSelection = () => {
      const pack = Number.parseInt(one('input[name="nightlevel-pack"]:checked')?.value || '1', 10);
      const baseSize = one('input[name="nightlevel-size"]:checked')?.value || 'regular';
      return { pack, baseSize, size: sizeForPack(pack, baseSize), colour: one('input[name="nightlevel-colour"]:checked')?.value || 'white' };
    };

    const setButtonState = (ready) => {
      const selectedPack = currentSelection().pack;
      all('[data-primary-atc], [data-submit-offer]').forEach((button) => {
        button.disabled = !ready;
        button.setAttribute('aria-disabled', String(!ready));
        const label = one('[data-atc-label]', button);
        if (label) label.textContent = ready ? packActionLabels[selectedPack] : 'Unavailable';
      });
    };

    const syncSelection = () => {
      const { size, baseSize, colour, pack } = currentSelection();
      const offer = offerMatrix[pack]?.[size] || offerMatrix[1].regular;
      const saving = offer.compare - offer.now;
      const selectedDiscountPercent = Math.round(((offer.compare - offer.now) / offer.compare) * 100);
      const colourLabel = colour === 'grey' ? 'Grey' : `${colour.charAt(0).toUpperCase()}${colour.slice(1)}`;
      const selectedLabel = `${colourLabel} · ${size.replace(/regular/g, 'Regular').replace(/high/g, 'High')} · ${pack} ${pack === 1 ? 'Pillow' : 'Pillows'}`;

      if (globalSizePicker) globalSizePicker.hidden = mixedBundlesReady;
      all('[data-bundle-sizes]').forEach((panel) => {
        const panelPack = Number(panel.dataset.bundleSizes);
        const showPanel = panelPack === pack && (mixedBundlesReady || panelPack > 1);
        panel.hidden = !showPanel;
        panel.classList.toggle('is-same-size', !mixedBundlesReady && panelPack > 1);
        const legend = one('legend', panel);
        if (legend) legend.textContent = mixedBundlesReady ? 'Choose each pillow size' : 'Choose one size for every pillow';
        if (!mixedBundlesReady && showPanel) {
          all('select', panel).forEach((select) => { select.value = baseSize; });
        }
        panel.closest('[data-pack-card]')?.classList.toggle('is-customising', !panel.hidden);
      });

      sizeInputs.forEach((input) => input.closest('label')?.classList.toggle('is-selected', input.checked));
      colourInputs.forEach((input) => input.closest('label')?.classList.toggle('is-selected', input.checked));
      packInputs.forEach((input) => input.closest('label')?.classList.toggle('is-selected', input.checked));
      const selectedColour = one('[data-selected-colour]');
      if (selectedColour) selectedColour.textContent = colourLabel;
      all('[data-live-price]').forEach((node) => { node.textContent = money(offer.now); });
      all('[data-compare-price]').forEach((node) => { node.textContent = money(offer.compare); });
      all('[data-saving]').forEach((node) => { node.textContent = `Save ${money(saving).replace('.00', '')}`; });
      all('[data-sticky-discount]').forEach((node) => { node.textContent = `Save ${selectedDiscountPercent}%`; });
      all('[data-selected-offer]').forEach((node) => { node.textContent = selectedLabel; });

      Object.entries(offerMatrix).forEach(([packKey, sizeOffers]) => {
        const rowOffer = sizeOffers[sizeForPack(Number(packKey), baseSize)] || sizeOffers.regular;
        const node = one(`[data-pack-price="${packKey}"]`);
        if (node) node.textContent = money(rowOffer.now);
        all(`[data-pack-compare="${packKey}"]`).forEach((packCompare) => {
          packCompare.textContent = money(rowOffer.compare);
        });
        all(`[data-pack-discount="${packKey}"]`).forEach((packDiscount) => {
          const discountPercent = Math.round(((rowOffer.compare - rowOffer.now) / rowOffer.compare) * 100);
          packDiscount.textContent = `Save ${discountPercent}%`;
        });
        const detail = one(`[data-pack-detail="${packKey}"]`);
        if (detail) {
          const packCount = Number(packKey);
          const perPillow = Math.round(rowOffer.now / packCount);
          detail.textContent = `${money(perPillow)} per pillow`;
        }
      });

      const match = findVariant(size, colour, pack);
      if (match && variantInput && quantityInput) {
        variantInput.value = String(match.variant.id);
        quantityInput.value = String(match.quantity);
        root.dataset.variantMode = match.mode;
        root.dataset.commerceStatus = previewMode ? 'preview-ready' : 'mapped';
        setButtonState(true);
      } else {
        if (variantInput) variantInput.value = '';
        if (quantityInput) quantityInput.value = '1';
        root.dataset.variantMode = 'unmapped';
        root.dataset.commerceStatus = 'blocked';
        // Say why, so a live check can tell a wrong price from a wrong catalogue shape.
        if (commerceMatrixReady) root.dataset.commerceReason = 'unavailable';
        else if (variants.length !== expectedCells.length) root.dataset.commerceReason = 'variant-count';
        else if (new Set(variantIds).size !== variantIds.length) root.dataset.commerceReason = 'duplicate-id';
        else root.dataset.commerceReason = 'cell-match';
        setButtonState(false);
      }
    };

    sizeInputs.forEach((input) => input.addEventListener('change', () => {
      bundleSizeInputs.forEach((select) => { select.value = input.value; });
      syncSelection();
    }));
    bundleSizeInputs.forEach((input) => input.addEventListener('change', () => {
      if (!mixedBundlesReady) {
        sizeInputs.forEach((sizeInput) => { sizeInput.checked = sizeInput.value === input.value; });
        bundleSizeInputs.forEach((select) => { select.value = input.value; });
      }
      syncSelection();
    }));
    colourInputs.forEach((input) => input.addEventListener('change', syncSelection));
    packInputs.forEach((input) => input.addEventListener('change', syncSelection));
    syncSelection();

    const submitProduct = (event) => {
      event?.preventDefault();
      if (!productForm || !primaryAtc || primaryAtc.disabled) return;
      productForm.requestSubmit(primaryAtc);
    };

    all('[data-submit-offer]').forEach((button) => button.addEventListener('click', submitProduct));

    const gallery = one('[data-gallery]');
    if (gallery) {
      const slides = all('[data-gallery-slide]', gallery);
      const thumbs = all('[data-gallery-thumb]', gallery);
      const current = one('[data-gallery-current]', gallery);
      const stage = one('[data-gallery-stage]', gallery);
      const thumbRail = one('[data-gallery-thumbs]', gallery);
      let index = 0;
      let pointerStart = null;
      let suppressImageClick = false;

      const activate = (nextIndex, focus = false) => {
        index = (nextIndex + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.classList.toggle('is-active', active);
          slide.hidden = !active;
          if (!active) all('video', slide).forEach((video) => video.pause());
        });
        thumbs.forEach((thumb, thumbIndex) => {
          const active = thumbIndex === index;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-pressed', String(active));
          thumb.tabIndex = active ? 0 : -1;
        });
        if (current) current.textContent = String(index + 1);
        const activeThumb = thumbs[index];
        if (activeThumb && thumbRail) {
          const targetLeft = activeThumb.offsetLeft - ((thumbRail.clientWidth - activeThumb.clientWidth) / 2);
          thumbRail.scrollTo({ left: Math.max(0, targetLeft), behavior: reduceMotion ? 'auto' : 'smooth' });
          if (focus) activeThumb.focus();
        }
      };

      thumbs.forEach((thumb, thumbIndex) => {
        thumb.addEventListener('click', () => activate(thumbIndex));
        thumb.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = thumbIndex;
          if (event.key === 'ArrowLeft') nextIndex -= 1;
          if (event.key === 'ArrowRight') nextIndex += 1;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = thumbs.length - 1;
          activate(nextIndex, true);
        });
      });

      slides.forEach((slide) => {
        one('img', slide)?.addEventListener('click', () => {
          if (suppressImageClick) return;
          activate(index + 1);
        });
      });
      stage?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') activate(index - 1);
        if (event.key === 'ArrowRight') activate(index + 1);
      });
      stage?.addEventListener('pointerdown', (event) => {
        suppressImageClick = false;
        pointerStart = { x: event.clientX, y: event.clientY };
      });
      stage?.addEventListener('pointerup', (event) => {
        if (!pointerStart) return;
        const distanceX = event.clientX - pointerStart.x;
        const distanceY = event.clientY - pointerStart.y;
        pointerStart = null;
        if (Math.abs(distanceX) < 45 || Math.abs(distanceX) <= Math.abs(distanceY)) return;
        suppressImageClick = true;
        setTimeout(() => { suppressImageClick = false; }, 0);
        activate(distanceX < 0 ? index + 1 : index - 1);
      });

      activate(0);
    }

    const bindVideoControls = (scope = root) => {
      all('[data-play-video]', scope).forEach((button) => {
        if (button.dataset.videoBound === 'true') return;
        button.dataset.videoBound = 'true';
        const video = button.previousElementSibling;
        if (!(video instanceof HTMLVideoElement)) return;
        const hydrate = () => {
          if (!video.src && video.dataset.videoSource) {
            video.src = video.dataset.videoSource;
            video.load();
          }
          video.controls = true;
        };
        button.addEventListener('click', async () => {
          hydrate();
          button.hidden = true;
          try {
            await video.play();
          } catch (_error) {
            button.hidden = false;
          }
        });
        video.addEventListener('play', () => { button.hidden = true; });
        video.addEventListener('ended', () => { button.hidden = false; });
      });
    };
    bindVideoControls();

    const reviewGrid = one('[data-review-grid]');
    const layoutReviewWall = () => {
      if (!reviewGrid) return;
      const gridStyle = getComputedStyle(reviewGrid);
      const rowHeight = Number.parseFloat(gridStyle.gridAutoRows) || 8;
      const rowGap = Number.parseFloat(gridStyle.rowGap) || 8;
      all('.sb-nightlevel__review', reviewGrid).forEach((review) => {
        if (getComputedStyle(review).display === 'none') {
          review.style.removeProperty('grid-row-end');
          return;
        }
        const span = Math.ceil((review.getBoundingClientRect().height + rowGap) / (rowHeight + rowGap));
        review.style.gridRowEnd = `span ${span}`;
      });
    };
    const bindReviewMedia = (scope = reviewGrid) => {
      if (!scope) return;
      all('img, video', scope).forEach((media) => {
        if (media.dataset.reviewLayoutBound === 'true') return;
        media.dataset.reviewLayoutBound = 'true';
        media.addEventListener('load', layoutReviewWall, { once: true });
        media.addEventListener('loadedmetadata', layoutReviewWall, { once: true });
      });
    };
    bindReviewMedia();
    window.addEventListener('resize', layoutReviewWall);
    requestAnimationFrame(layoutReviewWall);

    // Reviews arrive in three layers. Six render on first paint, ten more sit in an inert
    // template with their local media, and the rest of the library is a JSON asset that is
    // fetched once, only after a shopper asks for more, then revealed five at a time.
    const reviewButton = one('[data-show-reviews]');
    const reviewQueue = one('[data-review-queue]');
    const queuedReviews = reviewQueue instanceof HTMLTemplateElement
      ? Array.from(reviewQueue.content.children)
      : [];
    const reviewSource = reviewGrid?.dataset.reviewSource || '';
    const reviewStatus = one('[data-review-status]');
    const reviewBatchSize = 5;
    const inlineReviewCount = reviewGrid ? reviewGrid.querySelectorAll('.sb-nightlevel__review').length : 0;
    let nextReviewIndex = 0;
    let nextRemoteIndex = 0;
    let remoteReviews = null;
    let remoteReviewsPromise = null;

    // A record only becomes a card when it carries a whole-number rating from 1 to 5 and some
    // text, so a corrupted asset can never print invented five-star proof.
    const isUsableReview = (record) =>
      Boolean(record) && typeof record === 'object' && Number.isInteger(record.rating) &&
      record.rating >= 1 && record.rating <= 5 && typeof record.body === 'string' && record.body.trim().length > 0;

    // Resolves to the record list on success, or null when the fetch failed so the next
    // press retries instead of ending the wall on one bad request.
    const loadRemoteReviews = () => {
      if (!reviewSource || typeof fetch !== 'function') return Promise.resolve([]);
      if (remoteReviews) return Promise.resolve(remoteReviews);
      if (!remoteReviewsPromise) {
        remoteReviewsPromise = fetch(reviewSource, { credentials: 'omit' })
          .then((response) => (response.ok ? response.json() : Promise.reject(new Error(String(response.status)))))
          .then((records) => {
            remoteReviews = (Array.isArray(records) ? records : []).filter(isUsableReview);
            return remoteReviews;
          })
          .catch(() => {
            remoteReviewsPromise = null;
            return null;
          });
      }
      return remoteReviewsPromise;
    };

    const buildRemoteReview = (record) => {
      const article = document.createElement('article');
      article.className = 'sb-nightlevel__review';
      article.dataset.reviewId = String(record.id || '');
      article.dataset.moreReview = '';
      article.tabIndex = -1;
      const media = record.media;
      if (media && (media.type === 'photo' || media.type === 'video') && typeof media.src === 'string') {
        const mediaWrap = document.createElement('div');
        mediaWrap.className = 'sb-nightlevel__review-media';
        const width = Number.isFinite(Number(media.width)) ? Number(media.width) : 720;
        const height = Number.isFinite(Number(media.height)) ? Number(media.height) : 960;
        if (media.type === 'video') {
          const video = document.createElement('video');
          video.preload = 'none';
          video.playsInline = true;
          video.width = width;
          video.height = height;
          video.dataset.videoSource = media.src;
          if (typeof media.poster === 'string') video.poster = media.poster;
          const play = document.createElement('button');
          play.className = 'sb-nightlevel__play';
          play.type = 'button';
          play.setAttribute('aria-label', `Play ${String(record.name || 'customer')}’s video review`);
          play.dataset.playVideo = '';
          play.textContent = '▶';
          mediaWrap.append(video, play);
        } else {
          const image = document.createElement('img');
          image.src = media.src;
          image.alt = `${String(record.name || 'Nightlevel customer')}’s review photo`;
          image.width = width;
          image.height = height;
          image.loading = 'lazy';
          image.decoding = 'async';
          mediaWrap.append(image);
        }
        article.append(mediaWrap);
      } else {
        article.classList.add('sb-nightlevel__review--text');
      }
      const content = document.createElement('div');
      content.className = 'sb-nightlevel__review-content';
      const person = document.createElement('div');
      person.className = 'sb-nightlevel__review-person';
      const name = document.createElement('strong');
      name.textContent = String(record.name || 'Nightlevel customer');
      const label = document.createElement('span');
      label.textContent = String(record.label || 'Customer review');
      person.append(name, label);
      const meta = document.createElement('small');
      meta.textContent = [record.date, record.variant].filter(Boolean).join(' · ');
      const rating = record.rating;
      const stars = document.createElement('p');
      stars.className = 'sb-nightlevel__review-stars';
      stars.setAttribute('aria-label', `${rating} out of 5 stars`);
      stars.textContent = '★'.repeat(rating);
      if (rating < 5) {
        const empty = document.createElement('span');
        empty.setAttribute('aria-hidden', 'true');
        empty.textContent = '★'.repeat(5 - rating);
        stars.append(empty);
      }
      const copy = document.createElement('p');
      copy.className = 'sb-nightlevel__review-copy';
      copy.textContent = `“${String(record.body || '').trim()}”`;
      content.append(person, meta, stars, copy);
      article.append(content);
      return article;
    };

    const appendReviews = (reviews) => {
      const fragment = document.createDocumentFragment();
      reviews.forEach((review) => fragment.append(review));
      reviewGrid.append(fragment);
      reviewButton.setAttribute('aria-expanded', 'true');
      reviews.forEach((review) => {
        bindVideoControls(review);
        bindReviewMedia(review);
      });
      requestAnimationFrame(layoutReviewWall);
      if (reviewStatus) {
        const shown = reviewGrid.querySelectorAll('.sb-nightlevel__review').length;
        const total = remoteReviews ? inlineReviewCount + queuedReviews.length + remoteReviews.length : null;
        reviewStatus.textContent = total
          ? `${reviews.length} more reviews shown, ${shown} of ${total}`
          : `${reviews.length} more reviews shown, ${shown} so far`;
      }
      reviews[0]?.focus();
    };

    reviewButton?.addEventListener('click', async () => {
      if (!reviewGrid) {
        reviewButton.hidden = true;
        return;
      }
      const inlineBatch = queuedReviews.slice(nextReviewIndex, nextReviewIndex + reviewBatchSize);
      if (inlineBatch.length > 0) {
        nextReviewIndex += inlineBatch.length;
        appendReviews(inlineBatch.map((review) => review.cloneNode(true)));
        // Warm the JSON asset now so the next press already has it.
        const remote = await loadRemoteReviews();
        if (nextReviewIndex >= queuedReviews.length && remote && remote.length === 0) reviewButton.hidden = true;
        return;
      }
      reviewButton.disabled = true;
      const remote = await loadRemoteReviews();
      reviewButton.disabled = false;
      if (!remote) return; // The fetch failed, leave the button so the next press retries.
      const remoteBatch = remote.slice(nextRemoteIndex, nextRemoteIndex + reviewBatchSize);
      if (remoteBatch.length === 0) {
        reviewButton.hidden = true;
        return;
      }
      nextRemoteIndex += remoteBatch.length;
      appendReviews(remoteBatch.map(buildRemoteReview));
      if (nextRemoteIndex >= remote.length) reviewButton.hidden = true;
    });

    const sticky = one('[data-sticky-cta]');
    // The bar stops once the bottom of the last section (the FAQ) is on screen. The 1px
    // sentinel after it is only a fallback, a hidden element measures 0 x 0 at top 0 and
    // would read as "already on screen", so a rect without size is never trusted.
    const finalSection = one('[data-v1-section="faq"]') || one('[data-final-section]');
    if (sticky && primaryAtc) {
      sticky.hidden = false;
      const syncSticky = () => {
        const primaryPassed = primaryAtc.getBoundingClientRect().bottom <= 0;
        const finalRect = finalSection?.getBoundingClientRect();
        const finalOnScreen = Boolean(finalRect)
          && (finalRect.height > 0 || finalRect.width > 0)
          && finalRect.bottom <= window.innerHeight;
        sticky.classList.toggle('is-visible', primaryPassed && !finalOnScreen);
      };
      syncSticky();
      productForm?.addEventListener('change', syncSticky);
      window.addEventListener('scroll', syncSticky, { passive: true });
      window.addEventListener('resize', syncSticky, { passive: true });
    }
  });
})();
